import { addHours, differenceInMinutes } from "date-fns";
import React, { useEffect, useState } from "react";
import {
  buildStyles,
  CircularProgressbarWithChildren
} from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { useTranslation } from "react-i18next";
import { Button, Cell, Icon, Text, Tooltip } from "src/components";
import { IAgendaStep } from "src/types";
import { theme } from "src/config";
import styled from "styled-components";

type MeetingProgressProps = {
  title: string;
  percentage: number;
  duration: number;
  opened: Date;
  prevStep: IAgendaStep;
  curStep: IAgendaStep;
  nextStep: IAgendaStep;
  onNext?: () => void;
  onPrev?: () => void;
  onClick?: () => void;
};

export const MeetingProgress = (props: MeetingProgressProps) => {
  const { onNext, onPrev, onClick } = props;
  const { t } = useTranslation();
  const [minutesLeft, setMinutesLeft] = useState<number>(0);
  const [paused, setPaused] = useState<boolean>(false);

  const togglePlay = () => {
    setPaused(!paused);
  };

  useEffect(() => {
    const countDown = () => {
      const diffInMinutes = differenceInMinutes(
        addHours(props.opened, props.duration),
        new Date()
      );
      const clampedDifference = Math.max(0, diffInMinutes);
      setMinutesLeft(clampedDifference);
    };
    countDown(); // Start the count down
    const intervalId = setInterval(countDown, 6000); // Run every 60 seconds
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const minutesTotal = props.duration * 60;

  return (
    <ProgressBarWrapper onClick={onClick}>
      <AnimatedIcon size='large' color={theme.color.orange8} iconId='meeting' />
      {/* <AgendaControlContainer>
        <ControlIconsContainer>
          {props.prevStep && (
            <Tooltip placement="bottom" content={t("prev_agenda_step")}>
              <Button onClick={onPrev} variant="icon">
                <Icon size="large" iconId="skipPrev" />
              </Button>
            </Tooltip>
          )}
          <Tooltip
            placement="bottom"
            content={!paused ? t("start_meeting") : t("stop_meeting")}
          >
            <Button onClick={togglePlay} variant="icon">
              <Icon size="large" iconId={paused ? "pause" : "circlePlayIcon"} />
            </Button>
          </Tooltip>
          <Tooltip placement="bottom" content={t("next_agenda_step")}>
            <Button onClick={onNext} variant="icon">
              <Icon size="large" iconId="skipNext" />
            </Button>
          </Tooltip>
        </ControlIconsContainer>
        <Text
          size="small"
          weight="normal"
          color={theme.color.slate12}
          onClick={props.onClick}
        >
          {props.curStep ? props.curStep.title : props.title}
        </Text>
      </AgendaControlContainer> */}

      <ProgressBarContainer>
        <Tooltip content={t("meeting_progress")}>
          <CircularProgressbarWithChildren
            value={props.percentage}
            styles={buildStyles({
              strokeLinecap: "butt",
              pathColor: `${theme.color.orange7}`,
              textColor: `${theme.color.slate11}`,
              textSize: "14px"
            })}
          >
            <Text
              size="xxsmall"
              color={theme.color.grayDarkest}
              weight="bold"
            >{`${props.percentage}%`}</Text>
          </CircularProgressbarWithChildren>
        </Tooltip>
      </ProgressBarContainer>

      <ProgressBarContainer>
        <Tooltip content={t("meeting_time_progress")}>
          <CircularProgressbarWithChildren
            value={
              minutesTotal
                ? ((minutesTotal - minutesLeft) / minutesTotal) * 100
                : 100
            }
            styles={buildStyles({
              strokeLinecap: "butt",
              pathColor: `${theme.color.orange7}`,
              textColor: `${theme.color.slate11}`,
              textSize: "14px"
            })}
          >
            <Text size="xxsmall" color={theme.color.grayDarkest} weight="bold">
              {minutesLeft || 0}
            </Text>
            <ProgressBarText>
              <Text
                size="xxsmall"
                color={theme.color.grayDarkest}
                weight="bold"
              >{`${t("min")}`}</Text>
            </ProgressBarText>
          </CircularProgressbarWithChildren>
        </Tooltip>
      </ProgressBarContainer>

    </ProgressBarWrapper>
  );
};

const ProgressBarContainer = styled(Cell)`
  width:          3.4rem;
  display:        flex;
  flex-direction: row;
  margin-left:    0.5rem;
`;

const ProgressBarWrapper = styled(Cell)`
  display:         flex;
  justify-content: center;
  align-items:     center;
`;

const ProgressBarText = styled.div`
  display:        flex;
  flex-direction: column;
  align-items:    center;
  margin-top:     -0.3rem;
`;

const ControlIconsContainer = styled.div`
  display:        flex;
  flex-direction: row;
`;
const AgendaControlContainer = styled.div`
  display:        flex;
  flex-direction: column;
  align-items:    center;
  margin-left:    1rem;
`;

const AnimatedIcon = styled(Icon)`
  animation: pulse-animation 2s infinite;
  border-radius: 50%;
  margin-right: 1rem;

  @keyframes pulse-animation {
    0% {
      box-shadow: 0 0 0 0px rgba(219, 123, 8, 0.2);
    }
    100% {
      box-shadow: 0 0 0 12px rgba(219, 123, 8, 0);
    }
  }
`;
